import { SetStateAction, useCallback, useEffect, useRef, useState } from "react"

export function useSessionStorage(
  key: string,
  defaultValue?: string
): [string | undefined, (newValue: SetStateAction<string>) => void] {
  const [, setUpdateValue] = useState<string | undefined>()

  const realValue = getSessionStorage(key, defaultValue)
  const lastValue = useRef<string | undefined>(realValue)

  useEffect(() => {
    const changeListener = () => {
      const newValue = getSessionStorage(key, defaultValue)
      if (lastValue.current !== newValue) {
        lastValue.current = newValue
        setUpdateValue(newValue)
      }
    }
    window.addEventListener("storage", changeListener)
    return () => {
      window.removeEventListener("storage", changeListener)
    }
  }, [key])

  const setValue = useCallback(
    (newValue: SetStateAction<string>) => {
      if (typeof newValue === "function") {
        const oldValue = getSessionStorage(key, defaultValue)
        oldValue && setSessionStorage(key, newValue(oldValue))
        return
      }
      setSessionStorage(key, newValue)
    },
    [key]
  )

  return [realValue ?? undefined, setValue]
}

export function getSessionStorage(key: string, defaultValue?: string) {
  if (typeof window !== "undefined") {
    return window?.sessionStorage.getItem(key) ?? defaultValue
  }
  return defaultValue
}

export function setSessionStorage(key: string, newValue: string) {
  if (typeof window !== "undefined") {
    const oldValue = window.sessionStorage.getItem(key)
    if (oldValue === newValue) {
      return
    }
    window.sessionStorage.setItem(key, newValue)
    window.dispatchEvent(new Event("storage"))
  }
}
